// What goes on every invoice: your company details (a limited company has to
// show its number and registered office), bank details, numbering, payment
// terms and the email wording.
import { useState } from 'react';
import { api } from '../lib/auth';
import { Button, Input, Textarea, ModalShell } from '../lib/ui';
import { Label, errorText } from './TradeCommon';

export default function TradeInvoiceSettings({ settings, onClose, onSaved }) {
  const [f, setF] = useState(() => ({ ...settings }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => setF(prev => ({ ...prev, [k]: e.target.value }));
  const field = (k, label, { hint, ...props } = {}) => (
    <div><Label hint={hint}>{label}</Label><Input value={f[k] ?? ''} onChange={set(k)} {...props} /></div>
  );

  const nextNumber = `${f.prefix || ''}${String(Number(f.nextNumber) || 1).padStart(4, '0')}`;
  const missing = [
    !f.companyNumber && 'company number',
    !f.registeredOffice && 'registered office',
    !f.address && 'address',
    !(f.sortCode && f.accountNumber) && 'bank details',
  ].filter(Boolean);

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const { data } = await api.put('/trade/invoice-settings', f);
      onSaved(data);
    } catch (e) {
      setError(errorText(e));
      setSaving(false);
    }
  };

  return (
    <ModalShell onClose={onClose} width={680} ariaLabel="Invoice settings">
      <h2 style={{ margin: '0 0 4px', fontSize: 18 }}>Invoice settings</h2>
      <p className="muted" style={{ margin: '0 0 14px', fontSize: 13 }}>Printed on every invoice and used when emailing them.</p>

      {missing.length > 0 && (
        <div className="ui-banner ui-banner--warn" style={{ marginBottom: 12 }}>
          Still to fill in: {missing.join(', ')}.
        </div>
      )}

      <div className="trade-form">
        <h4>Your business</h4>
        <div className="inv-grid2">
          {field('companyName', 'Company name', { placeholder: 'Teardrop Hydroponics Ltd' })}
          {field('tradingName', 'Trading as', { placeholder: 'TearDrop Hydro' })}
          {field('phone', 'Phone')}
          {field('email', 'Email', { type: 'email', hint: 'replies and copies go here' })}
          {field('website', 'Website')}
        </div>
        <div><Label>Address</Label><Textarea rows={3} value={f.address || ''} onChange={set('address')} placeholder={'Unit or street\nHockley\nPostcode'} /></div>

        <h4>Company registration</h4>
        <div className="inv-grid2">
          {field('companyNumber', 'Company number', { placeholder: 'From Companies House' })}
          <div><Label>Registered office</Label><Textarea rows={2} value={f.registeredOffice || ''} onChange={set('registeredOffice')} /></div>
        </div>

        <h4>Bank details</h4>
        <div className="inv-grid3">
          {field('accountName', 'Account name')}
          {field('sortCode', 'Sort code', { placeholder: '00-00-00' })}
          {field('accountNumber', 'Account number', { placeholder: '8 digits' })}
        </div>

        <h4>Numbers and terms</h4>
        <div className="inv-grid3">
          {field('prefix', 'Number prefix', { placeholder: 'TDH-' })}
          {field('nextNumber', 'Next number', { type: 'number', min: 1, step: 1, hint: nextNumber })}
          {field('termsDays', 'Default terms', { type: 'number', min: 0, step: 1, hint: 'days' })}
        </div>

        <h4>Wording</h4>
        {field('vatNote', 'VAT line', { placeholder: 'No VAT is charged on this invoice.' })}
        {field('footerNote', 'Thank-you line')}
        {field('emailSubject', 'Email subject')}
        <div>
          <Label>Email message</Label>
          <Textarea rows={6} value={f.emailMessage || ''} onChange={set('emailMessage')} />
          <div className="trade-form__hint" style={{ marginTop: 6 }}>
            {'{{name}}'} is their first name, {'{{business}}'}, {'{{number}}'}, {'{{total}}'} and {'{{due}}'} fill in from the invoice.
          </div>
        </div>

        {error && <div className="ui-banner ui-banner--bad">{error}</div>}
      </div>
      <div className="trade-form__actions">
        <Button variant="muted" onClick={onClose}>Cancel</Button>
        <Button variant="primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save settings'}</Button>
      </div>
    </ModalShell>
  );
}
